import { canGrantBasicOrganizationAccess } from "./connection.js";
export const organizationRoleIds = [
    "organizationOwner",
    "organizationAdmin",
    "communicationsAdmin",
    "eventsAdmin",
    "eventCheckInStaff",
    "financeAdmin",
    "spacesAdmin",
    "volunteerCoordinator",
    "connectedPerson",
];
export const organizationPermissionIds = [
    "organization.readPrivate",
    "organization.updateProfile",
    "organization.manageCampuses",
    "connections.review",
    "connections.approve",
    "roles.read",
    "roles.assign",
    "roles.assignPrivileged",
    "people.disableAccount",
    "content.manage",
    "announcements.create",
    "announcements.publish",
    "events.manage",
    "events.checkIn",
    "finance.read",
    "finance.refund",
    "spaces.manage",
    "volunteers.manage",
    "directory.affiliations.manage",
    "settings.manage",
    "billing.read",
    "support.manage",
];
export const organizationRoleAssignmentStatuses = ["active", "revoked"];
export const organizationRoleAssignmentSources = [
    "development_seed",
    "migration_hvicc",
    "system_bootstrap",
    "trusted_backend",
];
export const platformSuperAdminRoleId = "platform_super_admin";
export const platformSupportRoleId = "platform_support";
export const platformRoleIds = [platformSuperAdminRoleId, platformSupportRoleId];
export const organizationRolePermissionMap = {
    connectedPerson: ["organization.readPrivate"],
    communicationsAdmin: [
        "organization.readPrivate",
        "content.manage",
        "announcements.create",
        "announcements.publish",
        "support.manage",
    ],
    eventCheckInStaff: ["organization.readPrivate", "events.checkIn"],
    eventsAdmin: ["organization.readPrivate", "events.manage", "events.checkIn"],
    financeAdmin: ["organization.readPrivate", "finance.read", "billing.read"],
    organizationAdmin: [
        "organization.readPrivate",
        "organization.updateProfile",
        "organization.manageCampuses",
        "connections.review",
        "connections.approve",
        "roles.read",
        "roles.assign",
        "people.disableAccount",
        "content.manage",
        "announcements.create",
        "announcements.publish",
        "events.manage",
        "events.checkIn",
        "spaces.manage",
        "volunteers.manage",
        "directory.affiliations.manage",
        "settings.manage",
        "support.manage",
    ],
    organizationOwner: [
        "organization.readPrivate",
        "organization.updateProfile",
        "organization.manageCampuses",
        "connections.review",
        "connections.approve",
        "roles.read",
        "roles.assign",
        "roles.assignPrivileged",
        "people.disableAccount",
        "content.manage",
        "announcements.create",
        "announcements.publish",
        "events.manage",
        "events.checkIn",
        "finance.read",
        "finance.refund",
        "spaces.manage",
        "volunteers.manage",
        "directory.affiliations.manage",
        "settings.manage",
        "billing.read",
        "support.manage",
    ],
    spacesAdmin: ["organization.readPrivate", "spaces.manage"],
    volunteerCoordinator: ["organization.readPrivate", "volunteers.manage"],
};
export const privilegedOrganizationRoleIds = organizationRoleIds.filter((roleId) => roleId !== "connectedPerson");
export const protectedOrganizationRoleIds = [
    "organizationOwner",
    "organizationAdmin",
    "financeAdmin",
];
export function organizationRoleAssignmentDocumentPath(organizationId, personId) {
    return `organizations/${organizationId}/roleAssignments/${personId}`;
}
export function organizationRoleAssignmentEventDocumentPath(organizationId, eventId) {
    return `organizations/${organizationId}/roleAssignmentEvents/${eventId}`;
}
export function buildOrganizationRoleAssignment(input) {
    const roleIds = normalizeOrganizationRoleIds(input.roleIds);
    return {
        assignedAt: input.timestamp,
        assignedBy: input.assignedBy,
        migration: {
            legacyAdminFlag: false,
            legacyFirebaseUid: null,
            legacySource: null,
            migratedAt: null,
            ...input.migration,
        },
        organizationId: input.organizationId,
        personId: input.personId,
        revokedAt: null,
        revokedBy: null,
        roleIds,
        source: input.source,
        status: "active",
        updatedAt: input.timestamp,
    };
}
export function buildRevokedOrganizationRoleAssignment(assignment, input) {
    return {
        ...assignment,
        revokedAt: input.timestamp,
        revokedBy: input.revokedBy,
        roleIds: [],
        status: "revoked",
        updatedAt: input.timestamp,
    };
}
export function buildOrganizationRoleAssignmentAuditEvent(input) {
    const roleIds = normalizeOrganizationRoleIds(input.roleIds);
    const eventId = input.eventId ?? `${input.targetPersonId}_${input.action}_${roleIds.join("_") || "none"}`;
    return {
        action: input.action,
        actorPersonId: input.actorPersonId,
        createdAt: input.timestamp,
        eventId,
        organizationId: input.organizationId,
        roleIds,
        source: input.source,
        targetPersonId: input.targetPersonId,
    };
}
export function hasOrganizationPermission(context, permissionId) {
    return permissionsForOrganizationAuthorizationContext(context).includes(permissionId);
}
export function permissionsForOrganizationAuthorizationContext(context) {
    return permissionsForOrganizationRoles(effectiveOrganizationRoleIds(context));
}
export function effectiveOrganizationRoleIds(context) {
    if (!context.principal ||
        !isActiveGlobalPerson(context.person, context.principal.uid) ||
        !isActiveOrganization(context.organization) ||
        !canGrantBasicOrganizationAccess(context.activeConnection)) {
        return [];
    }
    const roleIds = new Set(["connectedPerson"]);
    const assignment = context.roleAssignment;
    if (assignment?.status === "active" &&
        assignment.personId === context.principal.uid &&
        assignment.organizationId === organizationIdFromRecord(context.organization)) {
        for (const roleId of assignment.roleIds) {
            if (isOrganizationRoleId(roleId)) {
                roleIds.add(roleId);
            }
        }
    }
    return [...roleIds];
}
export function permissionsForOrganizationRoles(roleIds) {
    const permissions = new Set();
    for (const roleId of roleIds) {
        for (const permission of organizationRolePermissionMap[roleId]) {
            permissions.add(permission);
        }
    }
    return [...permissions];
}
export function authorizeOrganizationRoleAssignmentChange(input) {
    if (!input.actorContext.principal) {
        return { allowed: false, reason: "missing_principal" };
    }
    if (!canGrantBasicOrganizationAccess(input.actorContext.activeConnection)) {
        return { allowed: false, reason: "missing_active_connection" };
    }
    if (input.roleIds.length === 0 || input.roleIds.some((roleId) => !isOrganizationRoleId(roleId))) {
        return { allowed: false, reason: "invalid_role" };
    }
    const touchesProtectedRole = input.roleIds.some((roleId) => protectedOrganizationRoleIds.includes(roleId));
    const requiredPermission = touchesProtectedRole
        ? "roles.assignPrivileged"
        : "roles.assign";
    if (!hasOrganizationPermission(input.actorContext, requiredPermission)) {
        return { allowed: false, reason: "actor_lacks_assignment_authority" };
    }
    if (input.action === "revoke" &&
        input.roleIds.includes("organizationOwner") &&
        input.existingOwnerPersonIds.filter((personId) => personId !== input.targetPersonId).length ===
            0) {
        return { allowed: false, reason: "last_owner_revoke" };
    }
    return { allowed: true };
}
export function hasTrustedPlatformSuperAdminAuthority(input) {
    if (!input.principal || !isRecord(input.platformRole)) {
        return false;
    }
    const roleUid = input.platformRole.uid ?? input.platformRole.personId;
    return (roleUid === input.principal.uid &&
        input.platformRole.role === platformSuperAdminRoleId &&
        input.platformRole.status === "active");
}
export function hasTrustedPlatformSupportAuthority(input) {
    if (!input.principal || !isRecord(input.platformRole)) {
        return false;
    }
    const roleUid = input.platformRole.uid ?? input.platformRole.personId;
    return (roleUid === input.principal.uid &&
        platformRoleIds.includes(input.platformRole.role) &&
        input.platformRole.status === "active");
}
export function validateOrganizationRoleAssignmentRecord(assignment) {
    const errors = [];
    if (!isRecord(assignment)) {
        return { errors: ["Role assignment must be an object."], valid: false };
    }
    const organizationId = stringValue(assignment.organizationId);
    const personId = stringValue(assignment.personId);
    if (!isSafeDocumentId(organizationId)) {
        errors.push("Role assignment organizationId must be Firestore-safe.");
    }
    if (!isSafeDocumentId(personId)) {
        errors.push("Role assignment personId must be a Firestore-safe document ID.");
    }
    const roleIds = assignment.roleIds;
    if (!Array.isArray(roleIds) || !roleIds.every(isOrganizationRoleId)) {
        errors.push("Role assignment roleIds must contain supported V1 role IDs.");
    }
    if (!organizationRoleAssignmentStatuses.includes(assignment.status)) {
        errors.push("Role assignment status must be supported.");
    }
    if (assignment.status === "active" && Array.isArray(roleIds) && roleIds.length === 0) {
        errors.push("Active role assignment must contain at least one supported role ID.");
    }
    if (!organizationRoleAssignmentSources.includes(assignment.source)) {
        errors.push("Role assignment source must be supported.");
    }
    if ("email" in assignment || "platformRole" in assignment || "permissions" in assignment) {
        errors.push("Role assignment must not contain PII, platform authority, or raw permissions.");
    }
    return {
        errors,
        valid: errors.length === 0,
    };
}
export function isOrganizationRoleId(value) {
    return typeof value === "string" && organizationRoleIds.includes(value);
}
export function isOrganizationPermissionId(value) {
    return (typeof value === "string" &&
        organizationPermissionIds.includes(value));
}
export function legacyOrganizationRoleToV1Role(value) {
    switch (value) {
        case "connected_person":
            return "connectedPerson";
        case "organization_admin":
            return "organizationAdmin";
        case "owner":
            return "organizationOwner";
        default:
            return undefined;
    }
}
function normalizeOrganizationRoleIds(roleIds) {
    return [...new Set(roleIds)].filter(isOrganizationRoleId);
}
function isActiveGlobalPerson(person, uid) {
    return isRecord(person) && person.uid === uid && person.status === "active";
}
function isActiveOrganization(organization) {
    return isRecord(organization) && organization.status === "active";
}
function organizationIdFromRecord(organization) {
    if (!isRecord(organization)) {
        return "";
    }
    return stringValue(organization.organizationId) || stringValue(organization.id);
}
function isSafeDocumentId(value) {
    return value.length > 0 && value.length <= 150 && !value.includes("/");
}
function stringValue(value) {
    return typeof value === "string" ? value.trim() : "";
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
